import {
  Building2,
  CheckSquare,
  LayoutDashboard,
  MessagesSquare,
  NotebookTabs,
  Settings,
  ShieldAlert,
  Users,
} from "lucide-react";
import { NavLink, Outlet } from "react-router-dom";
import type { BootstrapConfig } from "@/lib/types";
import { clearAdminSecret } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const icons = {
  "layout-dashboard": LayoutDashboard,
  "building-2": Building2,
  users: Users,
  "check-square": CheckSquare,
  "notebook-tabs": NotebookTabs,
  "messages-square": MessagesSquare,
  "shield-alert": ShieldAlert,
  settings: Settings,
};

interface AppShellProps {
  config: BootstrapConfig;
  onLogout: () => void;
}

export function AppShell({ config, onLogout }: AppShellProps): import("react").ReactElement {
  return (
    <div className="min-h-screen">
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r bg-white/90 backdrop-blur lg:block">
        <div className="flex h-16 items-center border-b px-5">
          <div>
            <div className="text-sm font-semibold">{config.tenant.name}</div>
            <div className="text-xs text-muted-foreground">{config.branding.startupBanner}</div>
          </div>
        </div>
        <nav className="space-y-1 p-3">
          {config.navigation.map((item) => {
            const Icon = icons[item.icon as keyof typeof icons] ?? LayoutDashboard;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground",
                    "hover:bg-muted hover:text-foreground",
                    isActive && "bg-muted text-foreground",
                  )
                }
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b bg-white/85 px-4 backdrop-blur lg:px-6">
          <div>
            <div className="text-sm font-semibold">{config.tenant.name}</div>
            <div className="text-xs text-muted-foreground">Owner operations console</div>
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              clearAdminSecret();
              onLogout();
            }}
          >
            Lock
          </Button>
        </header>
        <nav className="flex gap-2 overflow-x-auto border-b bg-white px-4 py-2 lg:hidden">
          {config.navigation.map((item) => {
            const Icon = icons[item.icon as keyof typeof icons] ?? LayoutDashboard;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  cn(
                    "flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground",
                    "hover:bg-muted hover:text-foreground",
                    isActive && "bg-muted text-foreground",
                  )
                }
              >
                <Icon className="h-4 w-4" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <main className="mx-auto w-full max-w-7xl px-4 py-6 lg:px-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
